import { QueryReg } from '@/utils/regex'
import { debounce } from '@/utils/tools'
import AbstractEncoder from '@/utils/Encoders/AbstractEncoder'
import GlobalEncoder from '@/utils/Encoders/GlobalEncoder'

/**
 * @description
 * The QueryEncoder works as a wrapper around specific data, it will then automatically update the query in the url of
 * browsers to reflect the changes that will have occurred to the data. It will also automatically decode the current
 * url and update the data to reflect the changes.
 */
export default class QueryEncoder extends AbstractEncoder {
  private readonly _access: string
  private readonly _globalEncoder: typeof GlobalEncoder
  private readonly _addQueryKeyByDefault: boolean

  public get queryValue() {
    // the window.location.search can be changed out if we need a sort of dependency injection at some point
    return QueryEncoder.decodeQueryIntoObject(window.location.search)?.[this._access] ?? ''
  }

  /**
   * @description
   *  The class works by taking in initially checking if it is given any data. If not it will try to automatically
   *  decode the data from the query if possible. Therefore if you manually want to start decoding the data from the
   *  query, then give it an empty object or string initially.
   *  @example
   *  const a = new QueryEncoder({data = '', ...params}) // This will not do anything besides initializing a QueryEncoder
   *  console.log(a) // prints {}
   *  const b = new QueryEncoder({... params}) // This will try to decode the data
   *  console.log(b) // prints the output of the decoder function
   *
   * @param {any} data             - The data to wrap
   * @param {string} queryPoint    - The query name space used in the url (should be unique for the page)
   * @param {function} decoder     - The decoder used to decode the query string into usable data, if none is given it
   *                                 will just set the data to be the found string in the query
   * @param {function} encoder     - Encodes the data into a string, if none is given it will try to do so automatically
   * @param {any} defaultVal       - When decoding fails or does not find it's query endpoint it sets the data to this
   * @param {function} filter      - Allows you to filter out the data before it gets encoded for the url.
   *                                 The output of this function is passed to the encoder function
   * @param {boolean} decodeOnPop  - If it should call decode when detecting a popstate from the user
   * @param {Object} globalEncoder - This is an object that requires to have a push function it should take care of
   *                                 handling incoming request from multiple QueryEncoders and updating specific
   *                                 storage setups. The default works on the url of the browser. This should work
   *                                 like a dependency injector.
   * @param {{}}  args             - Any extra arguments get pushed to the parent class
   */
  constructor({
    data = null,
    queryPoint = '',
    decoder = null,
    encoder = null,
    defaultVal = null,
    filter = (_) => _,
    decodeOnPop = false,
    addQueryKeyByDefault = true,
    globalEncoder = GlobalEncoder,
    ...args
  } = {}) {
    super({ data, decoder, encoder, defaultVal, filter, ...args })
    this._access = queryPoint
    this._globalEncoder = globalEncoder
    this._addQueryKeyByDefault = addQueryKeyByDefault

    if (decodeOnPop) {
      // We bind ourselves to the event that occurs when the browser goes back in its history
      window.addEventListener('popstate', this.decode.bind(this))
    }

    // If we are the constructor we can safely decode now
    // If we don't have any data we will naively try to decode the data from the current query
    if (this.constructor === QueryEncoder && this._data === null) {
      this.decode()
    }
  }

  /**
   * @description
   *  Encodes the current data value into the url query
   *  @example
   *  const q = new QueryEncoder({
   *    data: ['hello', 'world']],
   *    queryPoint: 'q',
   *    filter(arr) => arr.filter(str) => str !== 'world'
   *  })
   *
   *  q.encode() // url now contains q=hello
   *  @affects
   *    GlobalEncoder
   */
  encode() {
    this.push(this._encode())
  }

  /**
   * @description
   *  This will try to decode from the url query and the localstorage the encoded data.
   *  It will then save the result in data
   */
  decode() {
    const queryVal = this.queryValue
    if (!queryVal && this._addQueryKeyByDefault) {
      // Nothing was found in url, so we push up the empty value to the url
      // this makes it so that every page load will always include this query string
      this.push(queryVal)
    }

    this._data = this._decode(queryVal)
  }

  push(query) {
    // We debounce this otherwise we might get lots of otherwise excess push request from class inheritance
    debounce(this._globalEncoder.push.bind(this._globalEncoder), 10)({ key: this._access, query })
  }

  /**
   * This function takes a query string like "?q=hello&a=this,is,an,array" and then converts it into an object
   * of key: value pairs, so the previous example would become {q: 'hello', a; 'this,is,an,array'}
   * @param {string} str
   * @returns {{}}
   * @constructor
   */
  static decodeQueryIntoObject(str = ''): any {
    const reg = new RegExp(QueryReg)
    const decoded = {}
    let workingResults: RegExpExecArray | null = null
    // eslint-disable-next-line no-cond-assign
    while ((workingResults = reg.exec(window.decodeURI(str)))) {
      // eslint-disable-next-line prefer-destructuring
      decoded[workingResults[1]] = window.decodeURIComponent(workingResults[2])
    }

    return decoded
  }
}
